import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './Auth.css';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(true);

  // Landing page'den gelen dil bilgisini uygula
  useEffect(() => {
    const savedLanguage = localStorage.getItem('rux_language');
    if (savedLanguage && savedLanguage !== i18n.language) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  // Token'ı doğrula
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError(t('error.MISSING_TOKEN'));
        setTokenValid(false);
        setTokenLoading(false);
        return;
      }

      try {
        const response = await axios.post('/auth/verify-reset-token', { token });
        if (response.data.success) {
          setTokenValid(true);
        } else {
          setError(t('error.INVALID_TOKEN'));
          setTokenValid(false);
        }
      } catch (err) {
        const errorCode = err.response?.data?.error_code;
        setError(errorCode ? t(`error.${errorCode}`) : t('error.INVALID_TOKEN'));
        setTokenValid(false);
      } finally {
        setTokenLoading(false);
      }
    };

    verifyToken();
  }, [token, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (formData.newPassword !== formData.confirmPassword) {
      setError(t('error.passwordMismatch'));
      return;
    }

    if (formData.newPassword.length < 8) {
      setError(t('error.passwordTooShort'));
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/auth/reset-password', {
        token,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword
      });

      if (response.data.success) {
        setSuccess(t(`success.${response.data.message_code}`));
        // 3 saniye sonra login sayfasına yönlendir
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(t('error.PASSWORD_RESET_FAILED'));
      }
    } catch (err) {
      const errorCode = err.response?.data?.error_code;
      setError(errorCode ? t(`error.${errorCode}`) : t('error.PASSWORD_RESET_FAILED'));
    } finally {
      setLoading(false);
    }
  };

  // Ana sayfaya dön
  const handleBackToHome = () => {
    window.location.href = '/';
  };

  // Token doğrulanıyor
  if (tokenLoading) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>{t('app.name')}</h1>
            <p>{t('auth.verifyingToken') || 'Token doğrulanıyor...'}</p>
          </div>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  // Token geçersiz
  if (!tokenValid) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>{t('app.name')}</h1>
            <p>{t('auth.resetPassword') || 'Şifre Sıfırla'}</p>
          </div>
          
          <div style={{ padding: '30px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
            <div className="error-message">{error}</div>
            
            <button 
              onClick={() => navigate('/login')}
              className="btn-primary"
              style={{ marginTop: '20px' }}
            >
              {t('auth.backToLogin') || 'Giriş Sayfasına Dön'}
            </button>
          </div>

          <div className="auth-footer" style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
            <button 
              onClick={handleBackToHome}
              style={{
                background: 'none',
                border: 'none',
                color: '#666',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: '14px'
              }}
            >
              ← {t('auth.backToHome') || 'Ana Sayfaya Dön'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Token geçerli - Şifre sıfırlama formu
  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>{t('app.name')}</h1>
          <p>{t('auth.resetPassword') || 'Yeni Şifre Belirle'}</p>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="newPassword">{t('auth.newPassword') || 'Yeni Şifre'}</label>
            <input
              id="newPassword"
              type="password"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              placeholder={t('auth.passwordMinLength')}
              required
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">{t('auth.confirmPassword')}</label>
            <input
              id="confirmPassword"
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder={t('auth.confirmPassword')}
              required
              disabled={loading}
            />
          </div>
          
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
          
          <button 
            type="submit" 
            disabled={loading || success} 
            className="btn-primary"
          >
            {loading ? t('auth.resetting') || 'Sıfırlanıyor...' : t('auth.resetPassword') || 'Şifreyi Sıfırla'}
          </button>
        </form>
        
        <div className="auth-footer">
          {t('auth.rememberPassword') || 'Şifrenizi hatırladınız mı?'}{' '}
          <a href="/login">{t('auth.login')}</a>
        </div>

        <div className="auth-footer" style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
          <button 
            onClick={handleBackToHome}
            style={{
              background: 'none',
              border: 'none',
              color: '#666',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: '14px'
            }}
          >
            ← {t('auth.backToHome') || 'Ana Sayfaya Dön'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
